import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { Player } from '@brightcove/web-sdk/ui';
import logger from '~/services/logger';

const CSAI_ALL_ADS_COMPLETED_EVENT = 'imaClientSideAllAdsCompleted';
const CSAI_AD_PAUSED_EVENT = 'imaClientSidePaused';
const CSAI_AD_RESUMED_EVENT = 'imaClientSideResumed';
const AD_CONTAINER_SELECTOR = '.vjs-ima3-ad-container';

/**
 * Manages CSAI lifecycle for the Brightcove player:
 * - Disables the IMA overlay (`pointer-events`) after ads complete so player controls are accessible.
 * - On ad click-through: reacts to `imaClientSidePaused` (fired after IMA opens the tab) to
 *   disable the overlay and register a player-container click to call `adsManager.resume()`.
 * - Calls `player.play()` when all ads finish to auto-start the main content.
 */
export function useCsai(
  player: Player | null,
  adTagUrl: string,
  containerRef: RefObject<HTMLDivElement | null>
): void {
  const isAdPausedRef = useRef(false);

  useEffect(() => {
    if (!player || !adTagUrl) return;

    const getAdsManager = (): any => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const integrationManager = (player as any).getIntegrationsManager();
        return integrationManager?.imaClientSideIntegration?.getAdsManager();
      } catch {
        return null;
      }
    };

    const getAdContainers = (): HTMLElement[] => {
      const container = containerRef.current;
      if (!container) return [];
      return Array.from(
        container.querySelectorAll<HTMLElement>(AD_CONTAINER_SELECTOR)
      );
    };

    const setAdContainerPointerEvents = (value: string): void => {
      getAdContainers().forEach((el) => {
        el.style.pointerEvents = value;
      });
    };

    const onPlayerContainerClick = (): void => {
      if (!isAdPausedRef.current) return;
      try {
        getAdsManager()?.resume();
      } catch (error) {
        logger.error(
          `useCsai: Error resuming ad on player click - ${
            (error as { message?: string })?.message ?? error
          }`
        );
      }
    };

    const onAdPaused = (): void => {
      isAdPausedRef.current = true;
      setAdContainerPointerEvents('none');
      containerRef.current?.addEventListener('click', onPlayerContainerClick);
    };

    const onAdResumed = (): void => {
      isAdPausedRef.current = false;
      setAdContainerPointerEvents('');
      containerRef.current?.removeEventListener(
        'click',
        onPlayerContainerClick
      );
    };

    const onAllAdsCompleted = (): void => {
      try {
        isAdPausedRef.current = false;
        setAdContainerPointerEvents('none');
        containerRef.current?.removeEventListener(
          'click',
          onPlayerContainerClick
        );
        player.play();
      } catch (error) {
        logger.error(
          `useCsai: Error restoring player controls after ads - ${
            (error as { message?: string })?.message ?? error
          }`
        );
      }
    };

    player.addEventListener(CSAI_AD_PAUSED_EVENT, onAdPaused);
    player.addEventListener(CSAI_AD_RESUMED_EVENT, onAdResumed);
    player.addEventListener(CSAI_ALL_ADS_COMPLETED_EVENT, onAllAdsCompleted);

    return () => {
      try {
        containerRef.current?.removeEventListener(
          'click',
          onPlayerContainerClick
        );
        player.removeEventListener(CSAI_AD_PAUSED_EVENT, onAdPaused);
        player.removeEventListener(CSAI_AD_RESUMED_EVENT, onAdResumed);
        player.removeEventListener(
          CSAI_ALL_ADS_COMPLETED_EVENT,
          onAllAdsCompleted
        );
      } catch (error) {
        logger.error(
          `useCsai: Error removing CSAI event listeners - ${
            (error as { message?: string })?.message ?? error
          }`
        );
      }
    };
  }, [player, adTagUrl, containerRef, isAdPausedRef]);
}
